import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { format } from "date-fns";
import PropTypes from "prop-types";

const today = new Date();
const minFileYearMonth = new Date(today.getFullYear() - 10, 0, 1);
const maxFileYearMonth = new Date(today.getFullYear() + 10, 11, 31);
const minFileYear = minFileYearMonth.getFullYear();
const maxFileYear = maxFileYearMonth.getFullYear();

const FileYearMonthPicker = ({
  value,
  onChange,
  placeholder = "Select date",
  buttonClassName = "category-dropdown-btn file-year-month-btn",
  containerClassName = "details-field-container file-year-month-field",
  labelClassName = "category-selected",
  placeholderClassName = "",
  portalToBody = false,
  forceDownward = false,
}) => {
  const [fileYearMonthViewDate, setFileYearMonthViewDate] = useState(
    () => value || new Date(),
  );
  const [showFileYearMonthPicker, setShowFileYearMonthPicker] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const pickerRef = useRef(null);
  const pickerPanelRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);

  useEffect(() => {
    if (value) {
      setFileYearMonthViewDate(value);
    } else {
      setFileYearMonthViewDate(new Date());
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        portalToBody &&
        pickerPanelRef.current &&
        pickerPanelRef.current.contains(event.target)
      ) {
        return;
      }
      if (
        showFileYearMonthPicker &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setShowFileYearMonthPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFileYearMonthPicker, portalToBody]);

  const updatePortalStyle = () => {
    if (!portalToBody || !showFileYearMonthPicker || !pickerRef.current) return;
    const triggerRect = pickerRef.current.getBoundingClientRect();
    setPortalStyle({
      position: "fixed",
      top: triggerRect.bottom + 4,
      left: triggerRect.left,
      width: triggerRect.width,
      zIndex: 10000,
    });
  };

  useEffect(() => {
    if (!showFileYearMonthPicker || !pickerRef.current) return;

    if (portalToBody) {
      updatePortalStyle();
      return;
    }

    if (forceDownward) {
      setOpenUpward(false);
      return;
    }

    const triggerRect = pickerRef.current.getBoundingClientRect();
    const panelHeight = pickerPanelRef.current?.offsetHeight || 300;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    setOpenUpward(spaceBelow < panelHeight && spaceAbove > spaceBelow);
  }, [showFileYearMonthPicker, portalToBody, forceDownward]);

  useEffect(() => {
    if (!portalToBody || !showFileYearMonthPicker) return;
    const handler = () => updatePortalStyle();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [portalToBody, showFileYearMonthPicker]);

  useEffect(() => {
    if (!showFileYearMonthPicker || !pickerRef.current) return;

    const triggerRect = pickerRef.current.getBoundingClientRect();
    const panelHeight = pickerPanelRef.current?.offsetHeight || 300;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    setOpenUpward(spaceBelow < panelHeight && spaceAbove > spaceBelow);
  }, [showFileYearMonthPicker]);

  const handleFileYearMonthChange = (e) => {
    const next = e.value;
    if (!next) {
      onChange(null);
      return;
    }
    const year = next.getFullYear();
    if (year < minFileYear || year > maxFileYear) {
      return;
    }
    onChange(next);
    setFileYearMonthViewDate(next);
    setShowFileYearMonthPicker(false);
  };

  const handleViewDateChange = (e) => {
    const next = e.value;
    if (!next) return;
    const year = next.getFullYear();
    if (year < minFileYear || year > maxFileYear) {
      return;
    }
    setFileYearMonthViewDate(next);
  };

  return (
    <div className={containerClassName} ref={pickerRef}>
      <Button
        outlined
        icon="pi pi-angle-down"
        iconPos="right"
        className={buttonClassName}
        onClick={() => setShowFileYearMonthPicker((prev) => !prev)}
      >
        <span
          className={`${labelClassName} ${
            !value && placeholderClassName ? placeholderClassName : ""
          }`}
        >
          {value ? format(value, "MMM yyyy") : placeholder}
        </span>
      </Button>
      {showFileYearMonthPicker &&
        (portalToBody ? (
          createPortal(
            <div
              ref={pickerPanelRef}
              style={portalStyle || undefined}
              className="file-year-month-container"
            >
              <Calendar
                value={value}
                onChange={handleFileYearMonthChange}
                view="month"
                inline
                minDate={minFileYearMonth}
                maxDate={maxFileYearMonth}
                viewDate={fileYearMonthViewDate}
                onViewDateChange={handleViewDateChange}
                className={`file-year-month-calendar-inline ${
                  fileYearMonthViewDate.getFullYear() <= minFileYear
                    ? "file-year-month-min-year"
                    : ""
                } ${
                  fileYearMonthViewDate.getFullYear() >= maxFileYear
                    ? "file-year-month-max-year"
                    : ""
                }`}
              />
            </div>,
            document.body,
          )
        ) : (
          <div
            ref={pickerPanelRef}
            className={`file-year-month-container ${
              openUpward ? "open-upward" : ""
            }`}
          >
            <Calendar
              value={value}
              onChange={handleFileYearMonthChange}
              view="month"
              inline
              minDate={minFileYearMonth}
              maxDate={maxFileYearMonth}
              viewDate={fileYearMonthViewDate}
              onViewDateChange={handleViewDateChange}
              className={`file-year-month-calendar-inline ${
                fileYearMonthViewDate.getFullYear() <= minFileYear
                  ? "file-year-month-min-year"
                  : ""
              } ${
                fileYearMonthViewDate.getFullYear() >= maxFileYear
                  ? "file-year-month-max-year"
                  : ""
              }`}
            />
          </div>
        ))}
    </div>
  );
};

FileYearMonthPicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  buttonClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  placeholderClassName: PropTypes.string,
  portalToBody: PropTypes.bool,
  forceDownward: PropTypes.bool,
};

export default FileYearMonthPicker;
